import React from 'react'
import { useDebounce } from 'react-use'
import useSWR from 'swr'
import { Likes } from 'types/Like'
import useCleanSlugUrl from './useCleanSlugUrl'
import useUserId from './useUserId'

interface ReturnType {
  isError: boolean
  isLikesLoading: boolean
  handleLikeClick: () => void
  totalLikes: number
  totalLikesByUser: number
}

const MAX_LIKES_ALLOWED_BY_USER = 8
const API_URL = `/api/likes`

async function getLikes(slug: string, type: string, userId: string): Promise<Likes> {
  const res = await fetch(API_URL + `/?slug=${slug}&type=${type}&userId=${userId}`)
  if (!res.ok) {
    throw new Error('An error occurred while fetching the data.')
  }
  return res.json()
}

async function updateLikes(
  slug: string,
  type: string,
  userId: string,
  count: number
): Promise<Likes> {
  const res = await fetch(API_URL + `/?slug=${slug}&type=${type}&userId=${userId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ count }),
  })

  if (!res.ok) {
    throw new Error('An error occurred while posting the data.')
  }

  return res.json()
}

// A custom hook to abstract away fetching and updating a user's likes
const useLikeCounter = ({ slug, type = 'blog' }: { slug: string; type: string }): ReturnType => {
  const cleanedSlug = useCleanSlugUrl(slug)
  const userId = useUserId()

  const { data, error, mutate } = useSWR<Likes, Error>(
    `/api/likes/?slug=${cleanedSlug}&type=${type}&userId=${userId}`,
    () => getLikes(cleanedSlug, type, userId),
    {
      dedupingInterval: 60000,
    }
  )

  const [batchedLikes, setBatchedLikes] = React.useState(0)

  const handleLikeClick = () => {
    // Prevent the user from liking more than MAX_LIKES_ALLOWED_BY_USER times
    if (!data || data.totalLikesByUser >= MAX_LIKES_ALLOWED_BY_USER) {
      return
    }

    // Optimistic ui pattern
    // update the local swr cache so like count updates immediately for the user
    // while we update the database
    mutate(
      {
        totalLikes: data.totalLikes + 1,
        totalLikesByUser: data.totalLikesByUser + 1,
      },
      false
    )

    // use local state and debounce to batch updates
    setBatchedLikes(batchedLikes + 1)
  }

  useDebounce(
    () => {
      if (batchedLikes === 0) return

      // update the database and use the data updatePostLikes returns to update
      // the local cache with database data
      mutate(updateLikes(cleanedSlug, type, userId, batchedLikes))
      setBatchedLikes(0)
    },
    1000,
    [batchedLikes]
  )

  return {
    totalLikesByUser: data?.totalLikesByUser,
    totalLikes: data?.totalLikes,
    isLikesLoading: !error && !data,
    isError: !!error,
    handleLikeClick,
  }
}

export default useLikeCounter
